import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Boxes,
  CircleDollarSign,
  Cpu,
  Gauge,
  Network,
} from "lucide-react";

import { api } from "@/api/client";
import { useEdgeStats } from "@/api/local/runtime";
import type { CostTimeseriesResult } from "@/api/types";
import { HealthBar } from "@/components/HealthBar";
import { MetricTile, type Tone } from "@/components/MetricTile";
import { Sparkline } from "@/components/Sparkline";
import {
  XdsHealthCard,
  type XdsNodeStatusOut,
} from "@/components/XdsHealthCard";
import { useAllReplicas, useServices } from "@/workloads/data";

/**
 * Per the operator's request: the Global dashboard is a graphical
 * summary only. Per-pool cost and recent events used to live here but
 * already have dedicated tabs (Cost, Events) in the SubNav, so they've
 * been removed to keep the dashboard at a glance.
 */
export function GlobalDashboard() {
  const services = useServices();
  const replicas = useAllReplicas();
  const edge = useEdgeStats();
  const costTs = useQuery({
    queryKey: ["cost", "timeseries", "global"],
    queryFn: () =>
      api.get<CostTimeseriesResult>("/cost/timeseries?buckets=24&bucket_seconds=3600"),
  });
  const xds = useQuery<XdsHealthOut>({
    queryKey: ["xds", "health"],
    queryFn: () => api.get<XdsHealthOut>("/xds/health"),
    staleTime: 4_000,
    refetchInterval: 5_000,
  });

  const replicaStats = useMemo(() => {
    const all = replicas.data ?? [];
    const ready = all.filter((r) => r.status === "ready").length;
    const provisioning = all.filter(
      (r) => r.status === "pending" || r.status === "provisioning",
    ).length;
    const failed = all.filter((r) => r.status === "failed").length;
    return { ready, provisioning, failed, total: all.length };
  }, [replicas.data]);

  const replicaTone: Tone =
    replicaStats.failed > 0
      ? "danger"
      : replicaStats.total === 0
        ? "neutral"
        : replicaStats.ready === replicaStats.total
          ? "ok"
          : "warn";

  const sparkValues = (costTs.data?.points ?? []).map((p) => p.cost_usd);

  const envoyTone: Tone =
    edge.envoy_replicas === 0
      ? "danger"
      : edge.envoy_replicas < edge.envoy_replicas_target
        ? "warn"
        : "ok";

  return (
    <>
    <div className="metric-grid">
      <MetricTile
        icon={<Network />}
        label="Envoy data plane"
        value={edge.envoy_replicas}
        unit={`/ ${edge.envoy_replicas_target} target`}
        tone={envoyTone}
        hint={
          edge.envoy_replicas === edge.envoy_replicas_target
            ? "all edge pods healthy"
            : `${edge.envoy_replicas_target - edge.envoy_replicas} below target`
        }
      />
      <MetricTile
        icon={<Activity />}
        label="Edge req/sec"
        value={edge.rps.toFixed(1)}
        unit="rps"
        tone="brand"
        hint={
          <Sparkline values={edge.rps_history} width={140} height={28} tone="brand" fill />
        }
        rows={[
          { label: "p50", value: `${edge.p50_ms.toFixed(0)} ms` },
          { label: "p99", value: `${edge.p99_ms.toFixed(0)} ms` },
          { label: "envoys", value: edge.envoy_replicas },
        ]}
      />
      <MetricTile
        icon={<Boxes />}
        label="Services"
        value={services.isLoading ? "—" : services.data?.length ?? 0}
        unit="active"
        tone="brand"
        hint="Logical workloads (model + routing)"
      />
      <MetricTile
        icon={<Cpu />}
        label="Replicas"
        value={`${replicaStats.ready}/${replicaStats.total}`}
        unit="ready"
        tone={replicaTone}
        hint={
          <HealthBar
            ready={replicaStats.ready}
            pending={replicaStats.provisioning}
            failed={replicaStats.failed}
            total={replicaStats.total}
            width={140}
          />
        }
        rows={[
          { label: "ready", value: replicaStats.ready },
          { label: "prov.", value: replicaStats.provisioning },
          { label: "failed", value: replicaStats.failed },
        ]}
      />
      <MetricTile
        icon={<CircleDollarSign />}
        label="Cost · 24h"
        value={`$${(costTs.data?.total_usd ?? 0).toFixed(2)}`}
        tone={(costTs.data?.total_usd ?? 0) > 0 ? "brand" : "neutral"}
        hint={
          <Sparkline values={sparkValues} width={140} height={28} tone="brand" fill />
        }
      />
      <MetricTile
        icon={<Gauge />}
        label="Avg req latency"
        value={`${edge.p50_ms.toFixed(0)}ms`}
        unit="p50"
        hint={`p99 ${edge.p99_ms.toFixed(0)} ms · synthetic until /metrics is wired`}
      />
      <MetricTile
        icon={<Activity />}
        label="xDS ACK p95"
        value={
          xds.data?.ack_latency_p95_ms != null
            ? `${xds.data.ack_latency_p95_ms.toFixed(0)} ms`
            : "—"
        }
        unit={`${xds.data?.connected_nodes ?? 0} envoys`}
        tone={
          xds.data?.has_nack
            ? "danger"
            : (xds.data?.ack_latency_p95_ms ?? 0) > 1000
              ? "warn"
              : "ok"
        }
        hint={
          xds.data?.has_nack
            ? "one or more Envoys NACKed the latest config"
            : `p50 ${xds.data?.ack_latency_p50_ms?.toFixed(0) ?? "—"} ms`
        }
      />
    </div>

    <div style={{ marginTop: 16 }}>
      <XdsHealthCard
        nodes={xds.data?.nodes ?? []}
        currentVersion={xds.data?.current_version ?? undefined}
        loading={xds.isLoading}
      />
    </div>
    </>
  );
}

/**
 * Mirrors the controller's `/xds/health` payload (added in the
 * per-pool SLO design — see
 * `controller/controller/api/routers/xds.py`). Aggregates per-node
 * status from `controller/controller/xds/snapshot.py` plus a few
 * derived stats (ACK latency percentiles, NACK presence) so the
 * dashboard tile renders with one query.
 */
interface XdsHealthOut {
  current_version: string | null;
  connected_nodes: number;
  has_nack: boolean;
  ack_latency_p50_ms: number | null;
  ack_latency_p95_ms: number | null;
  nodes: XdsNodeStatusOut[];
}
